import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { studentsApi, type AlertSeverity, type AtRiskStudent } from '../../api/students'

const REASON_ICON: Record<string, string> = {
  low_grades: '📉',
  behavior: '⚠️',
  absences: '🚫',
}

const SEVERITY_BG: Record<AlertSeverity, string> = {
  HIGH: 'bg-red-50 border-red-200 text-red-800',
  MEDIUM: 'bg-amber-50 border-amber-200 text-amber-800',
  LOW: 'bg-blue-50 border-blue-200 text-blue-800',
}

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  HIGH: 'bg-red-500',
  MEDIUM: 'bg-amber-500',
  LOW: 'bg-blue-500',
}

export function AtRiskWidget() {
  const { t } = useTranslation()
  const [days, setDays] = useState(30)
  const q = useQuery({
    queryKey: ['students', 'at-risk', days],
    queryFn: () => studentsApi.atRisk(days),
    staleTime: 5 * 60_000,
  })

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <span>🎯</span> {t('atRiskWidget.title')}
        </h2>
        <select
          className="input-field max-w-[120px] text-sm py-1"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
        >
          <option value="7">{t('atRiskWidget.days7')}</option>
          <option value="14">{t('atRiskWidget.weeks2')}</option>
          <option value="30">{t('atRiskWidget.days30')}</option>
          <option value="90">{t('atRiskWidget.months3')}</option>
        </select>
      </div>

      {q.isLoading && <div className="text-sm text-gray-500">{t('atRiskWidget.analyzing')}</div>}

      {q.isError && (
        <div className="text-sm text-red-600">{t('atRiskWidget.loadError')}</div>
      )}

      {q.data && q.data.students.length === 0 && (
        <div className="text-center py-6 text-gray-500 text-sm">
          ✓ {t('atRiskWidget.emptyState', { days })}
        </div>
      )}

      {q.data && q.data.students.length > 0 && (
        <div className="space-y-2">
          {q.data.students.slice(0, 8).map((s) => (
            <StudentRow key={s.studentId} s={s} />
          ))}
          {q.data.students.length > 8 && (
            <div className="text-xs text-gray-500 text-center pt-2">
              {t('atRiskWidget.andMore', { n: q.data.students.length - 8 })}
            </div>
          )}
        </div>
      )}

      {q.data && (
        <div className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">
          {t('atRiskWidget.updatedAt', { time: new Date(q.data.computedAt).toLocaleTimeString('ru') })}
        </div>
      )}
    </div>
  )
}

function StudentRow({ s }: { s: AtRiskStudent }) {
  const topSeverity = s.alerts.reduce<AlertSeverity>((acc, a) => {
    const rank = { HIGH: 0, MEDIUM: 1, LOW: 2 }
    return rank[a.severity] < rank[acc] ? a.severity : acc
  }, 'LOW')
  return (
    <Link
      to={`/app/students`}
      className={`block border rounded-lg px-3 py-2 hover:shadow-sm transition-shadow ${SEVERITY_BG[topSeverity]}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[topSeverity]}`} />
        <span className="font-medium">{s.fullName}</span>
        {s.className && (
          <span className="text-xs opacity-70">· {s.className}</span>
        )}
      </div>
      <div className="text-xs space-y-0.5 ml-4">
        {s.alerts.map((a, i) => (
          <div key={i}>
            <span className="mr-1">{REASON_ICON[a.reason] ?? '•'}</span>
            {a.message}
          </div>
        ))}
      </div>
    </Link>
  )
}
